import {
  WSButton,
  WSButtons,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { Currency, InvoiceRow } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { ErrorAlert } from "@components/ErrorAlert";
import { queriesInvoiceRows, queriesInvoices } from "@query";
import { InvoiceRemovalAction } from "./selectorInvoiceRemovalAction";

type Props = {
  invoice: InvoiceRow;
  /**
   * Which operation the user picked, resolved by the row menu through
   * `selectorInvoiceRemovalAction`. Passed in rather than re-derived here so
   * the label the user clicked and the mutation that runs come from one call
   * (PLA-3536) — a mismatch is now a type error rather than a silent drift.
   */
  action: InvoiceRemovalAction;
  onClose: () => void;
};

const INVOICE_LIST_DEPENDENCIES = [
  queriesInvoiceRows.searchInvoiceRows.queryKeyBase
];

export const ModalDelete: React.FC<Props> = props => {
  const [deleteInvoice, deleteInvoiceMeta] =
    queriesInvoices.deleteInvoice.useMutation({
      dependencies: INVOICE_LIST_DEPENDENCIES
    });
  const [cancelInvoice, cancelInvoiceMeta] =
    queriesInvoices.voidInvoice.useMutation({
      dependencies: INVOICE_LIST_DEPENDENCIES
    });

  const isCancel = props.action === "cancel";
  const dueDate = props.invoice.dueDate ?? new Date();
  const amount = props.invoice.amount ?? 0;
  const currency = props.invoice.currency ?? Currency.USD;
  const invoiceLabel = `${toWSDateString(
    dueDate,
    "monthDayYear"
  )} invoice for ${formatMoney(amount, currency)}`;

  return (
    <>
      <WSText.Heading5 mb="M">
        {isCancel ? "Cancel" : "Delete"} invoice
      </WSText.Heading5>
      <WSText mb="M">
        Are you sure you want to {isCancel ? "cancel" : "delete"} your{" "}
        {invoiceLabel}?
      </WSText>
      <WSText mb="XL">
        This action can't be undone, once you {isCancel ? "cancel" : "delete"}{" "}
        an invoice you'll have to recreate it.
      </WSText>

      {/* Without this a failed void/delete was fully silent: `useWSMutation`
          defaults `throwOnError` to false and there is no global handler, so
          the spinner just cleared and the modal stayed open. */}
      <ErrorAlert
        mb="XL"
        error={isCancel ? cancelInvoiceMeta.error : deleteInvoiceMeta.error}
        context="Other"
      />

      <WSButtons format="modal">
        <WSButton
          destructive
          name="delete"
          onClick={() => {
            const invoiceId = props.invoice.id;

            if (!invoiceId) {
              return;
            }

            if (isCancel) {
              cancelInvoice({ invoiceId }, { onSuccess: props.onClose });
            } else {
              deleteInvoice({ invoiceId }, { onSuccess: props.onClose });
            }
          }}
          loading={cancelInvoiceMeta.isLoading || deleteInvoiceMeta.isLoading}
        >
          {isCancel ? "Cancel" : "Delete"} invoice
        </WSButton>
        <WSButton.Secondary onClick={props.onClose}>
          Keep invoice
        </WSButton.Secondary>
      </WSButtons>
    </>
  );
};
